import React, { useContext } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Typography,
  Box,
  TextField,
  InputAdornment,
  Pagination,
  Stack,
  Select,
  MenuItem,
  IconButton
} from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { ColorModeContext } from "../../index";

const LIGHT_VARS = {
  "--vault-navy": "#0B2545",
  "--edge-blue": "#1F6FEB",
  "--canvas": "#FFFFFF",
  "--mist": "#F7F9FC",
  "--hairline": "#E4E9F0",
  "--blue-tint": "#EAF2FE",
  "--slate": "#6B7A90",
  "--secure": "#00A88F",
  "--high": "#E2622B",
  "--elevated": "#F0A202",
  "--critical": "#D6323A",
  "--border-radius": "8px",
};

const DARK_VARS = {
  "--vault-navy": "#F4F7FB",
  "--edge-blue": "#4C9AFF",
  "--canvas": "#0F1729",
  "--mist": "#252b3b",
  "--hairline": "rgba(255,255,255,0.14)",
  "--blue-tint": "rgba(76,154,255,0.16)",
  "--slate": "#93A4BD",
  "--secure": "#2FD3B8",
  "--high": "#F07C46",
  "--elevated": "#FFC24B",
  "--critical": "#FF6B72",
  "--border-radius": "8px",
};

export default function ReusableTable({
  columns,
  data,
  loading = false,
  emptyMessage = "No records found.",
  page = 0,
  rowsPerPage = 10,
  total = 0,
  onPageChange,
  onRowsPerPageChange,
  rowsPerPageOptions = [10, 25, 50],
  hidePagination = false,
  onSearch,
  searchPlaceholder = "Search...",
  filters,
  actions,
  tabs
}) {
  const { mode } = useContext(ColorModeContext || { mode: "light" });
  const V = mode === "dark" ? DARK_VARS : LIGHT_VARS;

  // Internal Pagination State
  const [internalPage, setInternalPage] = React.useState(0);
  const [internalRowsPerPage, setInternalRowsPerPage] = React.useState(rowsPerPage || 10);

  const isControlled = onPageChange !== undefined;
  const activePage = isControlled ? page : internalPage;
  const activeRowsPerPage = isControlled ? rowsPerPage : internalRowsPerPage;
  const activeTotal = isControlled ? total : (data ? data.length : 0);

  // Pagination calculation
  const totalPages = Math.ceil(activeTotal / activeRowsPerPage) || 1;
  const startItem = activeTotal === 0 ? 0 : activePage * activeRowsPerPage + 1;
  const endItem = Math.min((activePage + 1) * activeRowsPerPage, activeTotal);

  const displayData = isControlled ? data : (data || []).slice(activePage * activeRowsPerPage, activePage * activeRowsPerPage + activeRowsPerPage);

  const handlePageChange = (event, value) => {
    // our buttons send exact page value
    const newPage = value;
    if (isControlled) {
      if (onPageChange) onPageChange(event, newPage);
    } else {
      setInternalPage(newPage);
    }
  };

  const handleRowsPerPageChange = (event) => {
    const val = parseInt(event.target.value, 10);
    if (isControlled) {
      if (onRowsPerPageChange) onRowsPerPageChange(event);
    } else {
      setInternalRowsPerPage(val);
      setInternalPage(0);
    }
  };

  const tableContainerSx = {
    borderRadius: "12px",
    border: "1px solid var(--hairline)",
    backgroundColor: mode === "dark" ? "var(--mist)" : "var(--canvas)",
    boxShadow: mode === "dark" 
      ? "0 4px 20px rgba(0, 0, 0, 0.4)" 
      : "0 4px 20px rgba(0, 0, 0, 0.03)",
    overflowX: "auto",
    "& .MuiTable-root": {
      borderCollapse: "separate",
      borderSpacing: "0",
    },
    "& .MuiTableCell-root": {
      borderColor: "var(--hairline)",
      padding: "12px 16px",
      color: "var(--vault-navy)",
      fontFamily: "'Inter', 'Segoe UI', 'Roboto', 'Helvetica Neue', sans-serif",
      fontSize: "0.875rem",
    },
  };

  const tableHeadSx = {
    "& .MuiTableRow-root": {
      backgroundColor: mode === "dark" ? "rgba(255, 255, 255, 0.03)" : "transparent",
    },
    "& .MuiTableCell-head": {
      fontWeight: 600,
      fontSize: "0.75rem",
      color: "var(--slate)",
      textTransform: "uppercase",
      letterSpacing: "1px",
      borderBottom: "1px solid var(--hairline)",
      borderTop: "none",
    },
  };

  const tableRowHoverSx = {
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: mode === "dark" ? "rgba(255, 255, 255, 0.02)" : "#F4F7FB",
    },
    "& .MuiTableCell-root": {
      borderBottom: "1px solid var(--hairline)",
    },
    "&:last-child .MuiTableCell-root": {
      borderBottom: hidePagination ? "none" : "1px solid var(--hairline)",
    }
  };

  return (
    <Box style={V} sx={{ width: "100%" }}>
      <TableContainer component={Paper} elevation={0} sx={tableContainerSx}>
        
        {/* Toolbar Header */}
        {(onSearch || filters || actions || tabs) && (
          <Box sx={{ p: 2, borderBottom: '1px solid var(--hairline)' }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: tabs ? 2 : 0 }}>
              <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
                {onSearch && (
                  <TextField
                    size="small"
                    placeholder={searchPlaceholder}
                    onChange={(e) => onSearch(e.target.value)}
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" sx={{ color: 'var(--slate)' }} /></InputAdornment>,
                      sx: { 
                        borderRadius: '8px', 
                        backgroundColor: mode === 'dark' ? '#252b3b' : '#F4F7FB',
                        '& fieldset': { border: 'none' },
                        minWidth: '250px'
                      }
                    }}
                  />
                )}
                {filters}
              </Stack>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {actions}
              </Box>
            </Box>
            {tabs && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {tabs}
              </Box>
            )}
          </Box>
        )}

        <Table sx={{ minWidth: 650 }} aria-label="reusable unified table">
          <TableHead sx={tableHeadSx}>
            <TableRow>
              {columns.map((col, index) => (
                <TableCell
                  key={index}
                  align={col.align || "left"}
                  style={{ minWidth: col.minWidth, width: col.width }}
                >
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={columns.length} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={30} sx={{ color: "var(--edge-blue)" }} />
                </TableCell>
              </TableRow>
            ) : displayData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} align="center" sx={{ py: 6 }}>
                  <Typography variant="body1" sx={{ color: "var(--slate)", fontStyle: "italic" }}>
                    {emptyMessage}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              displayData.map((row, rowIndex) => (
                <TableRow key={rowIndex} sx={tableRowHoverSx}>
                  {columns.map((col, colIndex) => (
                    <TableCell key={colIndex} align={col.align || "left"}>
                      {col.render ? col.render(row, rowIndex) : row[col.id]}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        {/* Footer Pagination */}
        {!hidePagination && (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between', 
            p: 2, 
            borderTop: "1px solid var(--hairline)",
            flexWrap: 'wrap',
            gap: 2
          }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" sx={{ color: 'var(--slate)', fontSize: '0.875rem' }}>
                Rows per page:
              </Typography>
              <Select
                value={activeRowsPerPage}
                onChange={(e) => handleRowsPerPageChange(e)}
                size="small"
                variant="outlined"
                sx={{
                  borderRadius: '6px',
                  backgroundColor: 'transparent',
                  '& fieldset': { border: '1px solid var(--hairline)' },
                  '& .MuiSelect-icon': { color: 'var(--slate)' },
                  color: 'var(--vault-navy)',
                  height: '32px',
                  fontSize: '0.875rem',
                  minWidth: '60px'
                }}
              >
                {rowsPerPageOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </Stack>
            
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography variant="body2" sx={{ color: 'var(--slate)', fontSize: '0.875rem' }}>
                {startItem}-{endItem} of {activeTotal}
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 1 }}>
                <IconButton 
                  size="small"
                  onClick={(e) => handlePageChange(e, activePage - 1)}
                  disabled={activePage === 0}
                  sx={{ 
                    border: '1px solid var(--hairline)', 
                    borderRadius: '6px',
                    color: 'var(--slate)', 
                    '&:hover': { backgroundColor: 'var(--blue-tint)' },
                    '&.Mui-disabled': { opacity: 0.5 }
                  }}
                >
                  <KeyboardArrowLeftIcon fontSize="small" />
                </IconButton>
                <IconButton 
                  size="small"
                  onClick={(e) => handlePageChange(e, activePage + 1)}
                  disabled={activePage >= totalPages - 1 || activeTotal === 0}
                  sx={{ 
                    border: '1px solid var(--hairline)', 
                    borderRadius: '6px',
                    color: 'var(--slate)', 
                    '&:hover': { backgroundColor: 'var(--blue-tint)' },
                    '&.Mui-disabled': { opacity: 0.5 }
                  }}
                >
                  <KeyboardArrowRightIcon fontSize="small" />
                </IconButton>
              </Box>
            </Stack>
          </Box>
        )}
      </TableContainer>
    </Box>
  );
}
